import GatewayBase from './gateway-base';

type Plan = {
  revSharePercentage: number;
  billingCycle: string;
};

export default class BilingRestService extends GatewayBase {
  get plans() {
    return {
      find: this.getActivePlan,
      get: this.getPlans,
      update: this.updatePlan,
    };
  }

  get invoices() {
    return {
      admin: {
        charge: this.chargeInvoices,
        update: this.updateInvoices,
      },
      billCount: this.getBillCount,
      bills: this.getInvoiceBills,
      current: this.getCurrentInvoice,
      export: this.exportInvoice,
      get: this.getInvoices,
    };
  }

  get payouts() {
    return {
      admin: {
        get: this.getAdminPayouts,
        update: this.updatePayout,
      },
      bills: this.getPayoutBills,
      byId: this.getPayout,
      counts: this.getPayoutCounts,
      credited: this.getPayoutCredited,
      current: this.getCurrentPayout,
      discounts: this.getPayoutDiscounts,
      export: {
        credited: this.exportCreditedResolutions,
        discounts: this.exportDiscounts,
        revenueShare: this.exportRevenueShare,
        uncredited: this.exportUncreditedResolutions,
      },
      get: this.getPayouts,
      iframe: this.getIFrame,
      notCredited: this.getPayoutNotCredited,
      payee: this.getPayee,
    };
  }

  get corrections() {
    return {
      used: this.getPayoutCorrections,
    };
  }

  get billingMethods() {
    return {
      batchCreate: this.batchCreateBillingMethods,
      create: this.createBillingMethod,
      delete: this.deleteBillingMethod,
      get: this.getBillingMethods,
      uniqueByMerchant: this.getUniqueMethods,
      update: this.updateBillingMethod,
    };
  }

  get settings() {
    return {
      byStore: this.getBillingSettings,
      update: this.updateBillingSettings,
    };
  }

  private getActivePlan = (storeId: string) => {
    return this.client.get<Plan>(`v1/billing/${storeId}/plans/active`);
  };

  private getPlans = (storeId: string) => {
    return this.client.get<Plan>(`v1/billing/${storeId}/plans`);
  };

  private getInvoices = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/invoices`);
  };

  private getPayoutBills = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/bills`, { params });
  };

  private getInvoiceBills = (storeId: string, invoiceId: string, params: { page?: number; limit?: number }) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/bills`, { params });
  };

  private getPayoutCounts = (storeId: string, payoutId: string, params: { startDate?: Date; endDate?: Date }) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/counts`, { params });
  };

  private getBillCount = (storeId: string, invoiceId: string, params: { startDate?: Date; endDate?: Date }) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/bills/count`, { params });
  };

  private updateInvoices = (storeId: string, input) => {
    return this.client.put(`v1/billing/${storeId}/invoices/`, input);
  };

  private chargeInvoices = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/invoices/charge`, input);
  };

  private getPayoutCredited = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/credited`, { params });
  };

  private getPayoutNotCredited = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/not-credited`, { params });
  };

  private getPayoutDiscounts = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/discounts`, { params });
  };

  private getPayoutCorrections = (storeId: string, usedId: string) => {
    return this.client.get(`v1/billing/${storeId}/corrections/used/${usedId}`);
  };

  private getAdminPayouts = (storeId: string, usedId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/admin`);
  };

  private updatePayout = (storeId: string, payoutId: string, status: string) => {
    return this.client.put(`v1/billing/${storeId}/payouts/${payoutId}`, status);
  };

  private getPayouts = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts`);
  };

  private getPayout = (storeId: string, payoutId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}`);
  };

  private getCurrentPayout = (storeId: string, params?: { startDate?: Date; endDate?: Date; invoiceId?: string }) => {
    return this.client.get(`v1/billing/${storeId}/payouts/current`, { params });
  };

  private getIFrame = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/iframe`);
  };

  private getPayee = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/payee`);
  };

  private getBillingMethods = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/billing-methods`);
  };

  private createBillingMethod = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/billing-methods`, input);
  };

  private getUniqueMethods = (storeId: string, accountId: string) => {
    return this.client.get(`v1/billing/${storeId}/billing-methods/merchant/${accountId}/unique`);
  };

  private batchCreateBillingMethods = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/billing-methods/batch`, input);
  };

  private updateBillingMethod = (storeId: string, billingMethodId: string) => {
    return this.client.put(`v1/billing/${storeId}/billing-methods/${billingMethodId}`);
  };

  private deleteBillingMethod = (storeId: string, billingMethodId: string) => {
    return this.client.delete(`v1/billing/${storeId}/billing-methods/${billingMethodId}`);
  };

  private getCurrentInvoice = (storeId: string) => {
    return this.client.get<Plan>(`v1/billing/${storeId}/invoices/current`);
  };

  private updatePlan = (storeId: string, input: Plan) => {
    return this.client.put<Plan>(`v1/billing/${storeId}/plans`, input);
  };

  private getBillingSettings = (storeId: string) => {
    return this.client.get<{ exemptedEmails: string; id: string }>('v1/billing/settings', { params: { storeId } });
  };

  private updateBillingSettings = (id: string, updates: { exemptedEmails: string }) => {
    return this.client.put<{ exemptedEmails: string }>(`v1/billing/settings/${id}`, updates);
  };

  private exportInvoice = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/export`, body);
  };

  private exportRevenueShare = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/revenue-share/export`, body);
  };

  private exportDiscounts = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/discounts/export`, body);
  };

  private exportCreditedResolutions = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/credited/export`, body);
  };

  private exportUncreditedResolutions = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/not-credited/export`, body);
  };
}
